import { achievementsConfig } from '../config/achievementsConfig.js';
import { craftingRecipes, craftingQueueItemKeys } from '../config/craftingRecipesConfig.js';
import { calculateWeightedProbabilities } from '../lib/utils.js';
import { masterItemsConfig } from '../config/masterItemsConfig.js';
import { 
    GAME_TYPE_QUICK_CLICK, 
    GAME_TYPE_CAMPAIGN_CLICK, 
    GAME_TYPE_CAMPAIGN_ROLLING, 
    GAME_TYPE_AUTOSAVE_QUICK,
    GAME_TYPE_AUTOSAVE_CAMPAIGN,
    GAME_TYPE_AUTOSAVE_ROLLING
} from '../constants/gameTypes.js';

/**
 * @file This file contains template functions that generate HTML strings for various UI components.
 * @module viewTemplates
 */

/**
 * Generates HTML for game mode selection buttons.
 * @param {string} currentGameMode - The key of the currently selected game mode.
 * @returns {string} The generated HTML string.
 */
export function getGameModeHtml(currentGameMode) {
    const modes = [{ key: 'linear', name: 'Linear' }, { key: 'crafting', name: 'Crafting' }];
    return `<h3>Game Mode</h3>${generateButtonOptions(modes, currentGameMode, 'mode', 'game-mode-selector')}`;
}


/**
 * Generates a set of button options.
 * @param {Array<object>} options - Array of option objects, each with `key` and `name`.
 * @param {string} currentKey - The key of the currently active option.
 * @param {string} dataAttribute - The `data-*` attribute to use for the key.
 * @param {string} cssClass - The CSS class to apply to each button.
 * @returns {string} The generated HTML string.
 */
function generateButtonOptions(options, currentKey, dataAttribute, cssClass) {
    const buttons = options.map(option => {
        const isActive = option.key === currentKey ? 'active-setting' : '';
        return `<button data-${dataAttribute}="${option.key}" class="button-style button-config ${cssClass} ${isActive}">${option.name}</button>`;
    }).join('');

    return `<div class="flex gap-2 mb-4 flex-wrap">${buttons}</div>`;
}

/**
 * Generates HTML for grid size selection buttons.
 * @param {Array<object>} availableGridSizes - Array of grid size options.
 * @param {number} currentSize - The currently selected grid size.
 * @returns {string} The generated HTML string.
 */
export function getGridSizeHtml(availableGridSizes, currentSize) {
    const buttons = availableGridSizes.map(sizeInfo => {
        const isActive = sizeInfo.key === currentSize ? 'active-setting' : '';
        return `<button data-size="${sizeInfo.key}" class="button-style button-config grid-size-selector ${isActive}">${sizeInfo.name} (${sizeInfo.key}x${sizeInfo.key})</button>`;
    }).join('');

    return `<h3>Grid Size</h3>
            <div class="flex gap-2 mb-4 flex-wrap">${buttons}</div>`;
}

/**
 * Generates HTML for biome selection buttons.
 * @param {object} biomes - The biomes configuration object.
 * @param {string} currentBiomeKey - The key of the currently selected biome.
 * @returns {string} The generated HTML string.
 */
export function getBiomesHtml(biomes, currentBiomeKey) {
    const buttons = Object.keys(biomes).map(biomeKey => {
        const biome = biomes[biomeKey];
        const isActive = biomeKey === currentBiomeKey ? 'active-setting' : '';
        return `<button data-biome="${biomeKey}" class="button-style button-config biome-selector ${isActive}">${biome.name}</button>`;
    }).join('');

    return `<div class="flex gap-2 mb-4 flex-wrap">${buttons}</div>`;
}

/**
 * Generates HTML for path length selection buttons.
 * @param {Array<object>} pathLengthsArray - Array of path length options.
 * @param {string} currentLengthKey - The key of the currently selected path length.
 * @returns {string} The generated HTML string.
 */
export function getPathLengthOptionsHtml(pathLengthsArray, currentLengthKey) {
    return generateButtonOptions(pathLengthsArray, currentLengthKey, 'length', 'path-length-selector');
}

/**
 * Generates HTML for the limited turns toggle switch.
 * @param {boolean} isLimitedTurnsMode - Whether the mode is currently enabled.
 * @param {number} defaultTurnLimit - The default number of turns.
 * @returns {string} The generated HTML string.
 */
export function getLimitedTurnsToggleHtml(isLimitedTurnsMode, defaultTurnLimit) {
    return `
        <h3>Limited Turns</h3>
        <div class="flex gap-2 mb-4">
            <label class="inline-flex items-center cursor-pointer">
                <input type="checkbox" id="limited-turns-toggle" class="sr-only peer" ${isLimitedTurnsMode ? 'checked' : ''}>
                <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                <span class="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">Enable Limited Turns (Default: ${defaultTurnLimit} turns)</span>
            </label>
        </div>
    `;
}

/**
 * Generates HTML for the enable animals toggle switch.
 * @param {boolean} isAnimalsEnabled - Whether animals are currently enabled.
 * @returns {string} The generated HTML string.
 */
export function getEnableAnimalsToggleHtml(isAnimalsEnabled) {
    return `
        <h3>Enable Animals</h3>
        <div class="flex gap-2 mb-4">
            <label class="inline-flex items-center cursor-pointer">
                <input type="checkbox" id="disable-animals-toggle" class="sr-only peer" ${isAnimalsEnabled ? 'checked' : ''}>
                <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                <span class="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">Enable animals that block grid cells</span>
            </label>
        </div>
    `;
}

/**
 * Generates HTML for the disasters toggle switch.
 * @param {boolean} isEnabled - Whether disasters are currently enabled.
 * @returns {string} The generated HTML string.
 */
export function getDisastersToggleHtml(isEnabled) {
    return `
        <h3>Enable Disasters</h3>
        <div class="flex gap-2 mb-4">
            <label class="inline-flex items-center cursor-pointer">
                <input type="checkbox" id="disasters-toggle" class="sr-only peer" ${isEnabled ? 'checked' : ''}>
                <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                <span class="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">Enable random disasters</span>
            </label>
        </div>
    `;
}

/**
 * Generates HTML for the random queue toggle switch.
 * @param {boolean} isEnabled - Whether the random queue is currently enabled.
 * @returns {string} The generated HTML string.
 */
export function getRandomQueueToggleHtml(isEnabled) {
    return `
        <h3>Random Item Queue</h3>
        <div class="flex gap-2 mb-4">
            <label class="inline-flex items-center cursor-pointer">
                <input type="checkbox" id="random-queue-toggle" class="sr-only peer" ${isEnabled ? 'checked' : ''}>
                <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                <span class="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">Enable random item queue (if off, only base items appear)</span>
            </label>
        </div>
    `;
}

/**
 * Generates HTML for the stationary objects toggle switch.
 * @param {boolean} isEnabled - Whether stationary objects are currently enabled.
 * @returns {string} The generated HTML string.
 */
export function getStationaryObjectsToggleHtml(isEnabled) {
    return `
        <h3>Enable Obstacles</h3>
        <div class="flex gap-2 mb-4">
            <label class="inline-flex items-center cursor-pointer">
                <input type="checkbox" id="stationary-objects-toggle" class="sr-only peer" ${isEnabled ? 'checked' : ''}>
                <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                <span class="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">Enable stationary obstacles</span>
            </label>
        </div>
    `;
}

/**
 * Generates HTML for the power-ups toggle switch.
 * @param {boolean} isEnabled - Whether power-ups are currently enabled.
 * @returns {string} The generated HTML string.
 */
export function getPowerupsToggleHtml(isEnabled) {
    return `
        <h3>Enable Power-ups</h3>
        <div class="flex gap-2 mb-4">
            <label class="inline-flex items-center cursor-pointer">
                <input type="checkbox" id="powerups-toggle" class="sr-only peer" ${isEnabled ? 'checked' : ''}>
                <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                <span class="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">Enable power-up items in queue</span>
            </label>
        </div>
    `;
}

/**
 * Generates HTML for the Settlers Mode toggle switch.
 * @param {boolean} isEnabled - Whether Settlers Mode is currently enabled.
 * @returns {string} The generated HTML string.
 */
export function getSettlersModeToggleHtml(isEnabled) {
    return `
        <h3 class="text-lg font-semibold text-yellow-400">Game Modifier</h3>
        <div class="flex gap-2 mb-4">
            <label class="inline-flex items-center cursor-pointer">
                <input type="checkbox" id="settlers-mode-toggle" class="sr-only peer" ${isEnabled ? 'checked' : ''}>
                <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                <span class="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">Enable Settlers Mode (fills board with more settlements)</span>
            </label>
        </div>
    `;
}

/**
 * Generates HTML for the NPC wandering toggle switch.
 * @param {boolean} isEnabled - Whether NPC wandering is currently enabled.
 * @returns {string} The generated HTML string.
 */
export function getNpcWanderingToggleHtml(isEnabled) {
    return `
        <h3>NPC Wandering</h3>
        <div class="flex gap-2 mb-4">
            <label class="inline-flex items-center cursor-pointer">
                <input type="checkbox" id="npc-wandering-toggle" class="sr-only peer" ${isEnabled ? 'checked' : ''}>
                <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                <span class="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">Enable NPC wandering</span>
            </label>
        </div>
    `;
}

/**
 * Generates HTML for the weather effects toggle switch.
 * @param {boolean} isEnabled - Whether weather effects are currently enabled.
 * @returns {string} The generated HTML string.
 */
export function getWeatherEffectsToggleHtml(isEnabled) {
    return `
        <h3>Weather Effects</h3>
        <div class="flex gap-2 mb-4">
            <label class="inline-flex items-center cursor-pointer">
                <input type="checkbox" id="weather-effects-toggle" class="sr-only peer" ${isEnabled ? 'checked' : ''}>
                <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                <span class="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">Enable visual weather effects (rain, snow, etc.)</span>
            </label>
        </div>
    `;
}

/**
 * Generates HTML for the ambient animals toggle switch.
 * @param {boolean} isEnabled - Whether ambient animals are currently enabled.
 * @returns {string} The generated HTML string.
 */
export function getAmbientAnimalsToggleHtml(isEnabled) {
    return `
        <h3>Ambient Animals</h3>
        <div class="flex gap-2 mb-4">
            <label class="inline-flex items-center cursor-pointer">
                <input type="checkbox" id="ambient-animals-toggle" class="sr-only peer" ${isEnabled ? 'checked' : ''}>
                <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                <span class="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">Enable ambient animals (birds, etc.)</span>
            </label>
        </div>
    `;
}

/**
 * Generates HTML for the 3D effects toggle switch.
 * @param {boolean} isEnabled - Whether 3D effects are currently enabled.
 * @returns {string} The generated HTML string.
 */
export function get3dEffectsToggleHtml(isEnabled) {
    return `
        <h3>3D Effects</h3>
        <div class="flex gap-2 mb-4">
            <label class="inline-flex items-center cursor-pointer">
                <input type="checkbox" id="d3-effects-toggle" class="sr-only peer" ${isEnabled ? 'checked' : ''}>
                <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                <span class="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">Enable 3D perspective effects</span>
            </label>
        </div>
    `;
}

/**
 * Generates HTML for the colorblind mode toggle switch.
 * @param {boolean} isEnabled - Whether colorblind mode is currently enabled.
 * @returns {string} The generated HTML string.
 */
export function getColorblindModeToggleHtml(isEnabled) {
    return `
        <h3>Colorblind Mode</h3>
        <div class="flex gap-2 mb-4">
            <label class="inline-flex items-center cursor-pointer">
                <input type="checkbox" id="colorblind-mode-toggle" class="sr-only peer" ${isEnabled ? 'checked' : ''}>
                <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                <span class="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">Enable colorblind-friendly patterns</span>
            </label>
        </div>
    `;
}
/**
 * Generates HTML for the "How to Play" rules section of the guide.
 * @param {object} textConfig - The text configuration object.
 * @returns {string} The generated HTML string.
 */
export function getGuideRulesHtml(textConfig) {
    const rulesList = textConfig.guide.rules.map(rule => `<li>${rule}</li>`).join('');
    return `
        <h3>${textConfig.guide.howToPlayTitle}</h3>
        <ul class="list-disc list-inside">${rulesList}</ul>
    `;
}

/**
 * Generates HTML for the audio test case buttons.
 * @param {object} sounds - The sounds object from the audio configuration.
 * @returns {string} The generated HTML string.
 */
export function getAudioTestCasesHtml(sounds) {
    if (!sounds || Object.keys(sounds).length === 0) {
        return '<p>No sounds configured.</p>';
    }
    return '<div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">' +
    Object.keys(sounds).map(soundKey => `
        <button data-sound-key="${soundKey}" class="button-style play-sound-btn">${soundKey}</button>
    `).join('') + '</div>';
}

/**
 * Generates HTML for the test cases list.
 * @param {Array<object>} tests - An array of test case objects.
 * @returns {string} The generated HTML string.
 */
export function getTestCasesHtml(tests) {
    if (!tests || tests.length === 0) {
        return '<p>No tests defined.</p>';
    }
    return tests.map(test => `
        <div class="test-case-item flex items-center justify-between p-3 border-b border-gray-600">
            <div>
                <h4 class="font-bold">${test.name}</h4>
                <p class="text-sm text-gray-400">${test.description}</p>
            </div>
            <button data-test-id="${test.id}" class="button-style run-test-btn">Run Test</button>
            <div id="result-${test.id}" class="test-result font-bold ml-4 w-48 text-left"></div>
        </div>
    `).join('');
}

/**
 * Generates HTML for the campaign progression list in the guide.
 * @param {Array<object>} levels - An array of campaign level objects.
 * @param {object} biomes - The biomes configuration object.
 * @param {object} gameOptions - The game options configuration object.
 * @returns {string} The generated HTML string.
 * @param {object} flatMasterItems - A flattened object of all master items.
 * @param {object} textConfig - The text configuration object.
 */
export function getCampaignProgressionVisualisationHtml(levels, biomes, gameOptions, flatMasterItems, textConfig) {
    const levelCards = levels.map((level, index) => {
        const biomeName = biomes[level.biomeKey]?.name || 'Unknown Biome';
        const sizeInfo = gameOptions.gridSizes.find(g => g.key === level.gridSize);
        const gridSizeName = sizeInfo ? sizeInfo.name : `${level.gridSize}x${level.gridSize}`;
        const pathInfo = biomes[level.biomeKey]?.upgradePathLinear.find(p => p.key === level.pathLength);
        const pathLengthName = pathInfo ? pathInfo.name : 'Unknown Path';

        // Find the final item of the upgrade path for this level
        const finalItemKey = pathInfo?.path[pathInfo.path.length - 1];
        const finalItem = finalItemKey ? flatMasterItems[finalItemKey] : null;

        const finalItemHtml = finalItem ? `
            <div class="flex items-center mt-2">
                <div class="queue-gem mr-2" style="background-color: ${finalItem.style.backgroundColor};">${finalItem.display}</div>
                <span class="text-sm text-gray-300">Final Item: ${finalItem.name}</span>
            </div>
        ` : '';

        return `
            <div class="bg-gray-700 p-4 rounded-lg flex flex-col">
                <h4 class="font-bold text-lg text-yellow-300">Level ${index + 1}</h4>
                <div class="flex-grow">
                    <p class="text-sm text-gray-400">${biomeName}</p>
                    <p class="text-sm text-gray-400">${gridSizeName} Grid</p>
                    <p class="text-sm text-gray-400">${pathLengthName} Path</p>
                    <p class="text-sm text-gray-400">Target: ${level.targetScore.toLocaleString()}</p>
                    ${finalItemHtml}
                </div>
            </div>
        `;
    }).join('');

    return `
        <hr class="my-8 border-gray-600">
        <h3 class="text-2xl font-bold">${textConfig.guide.campaignProgressionTitle}</h3>
        <p class="mb-2 text-gray-400">The campaign follows this level order.</p>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            ${levelCards}
        </div>
    `;
}

/**
 * Generates HTML for the items in the current game configuration.
 * @param {object} config - The active game configuration.
 * @returns {string} The generated HTML string.
 */
export function getGameItemsHtml(config) {
    const createItemCard = (item, subtext = '') => `
        <div class="bg-gray-700 p-3 rounded-lg flex items-center">
            <div class="queue-gem mr-3" style="background-color:${item.style.backgroundColor};">${item.display}</div>
            <div class="flex-grow">
                <div class="font-bold">${item.name}</div>
                ${subtext ? `<div class="text-sm text-gray-400">${subtext}</div>` : ''}
            </div>
        </div>
    `;

    let upgradePathItems;

    // The guide always shows the linear path, so we can calculate odds based on that.
    const placeableItems = config.upgradePath.slice(0, -1);
    if (placeableItems.length > 0) {
        // Calculate weighted probabilities for item spawning
        const { weights, totalWeight } = calculateWeightedProbabilities(placeableItems);

        upgradePathItems = config.upgradePath.map((item, index) => {
            let subtext = `Tier ${index + 1}`;
            // Only placeable items (not the final one) have a spawn chance
            if (index < placeableItems.length) {
                const percentage = ((weights[index] / totalWeight) * 100).toFixed(1);
                subtext += ` - ${percentage}% Chance`;
            }

            return `
                <div class="bg-gray-700 p-4 rounded-lg flex flex-col">
                    <h4 class="font-bold text-lg text-yellow-300">${item.name}</h4>
                    <div class="flex items-center mt-2">
                        <div class="queue-gem mr-2" style="background-color: ${item.style.backgroundColor};">${item.display}</div>
                        <span class="text-sm text-gray-300">${subtext}</span>
                    </div>
                </div>
            `;
        }).join('');
    } else {
        // Fallback for empty or very short paths
        upgradePathItems = config.upgradePath.map(item => createItemCard(item)).join('');
    }

    const animalItems = (config.animals && config.animals.length > 0) ?
        `<h3 class="mt-6">Animals</h3><div class="grid grid-cols-1 md:grid-cols-3 gap-4">${config.animals.map(item => createItemCard(item)).join('')}</div>`
        : '';

    const specialItems = config.genericUpgradePath.map(item => createItemCard(item)).join('');

    return `<h3 class="mt-4 text-2xl font-bold">Upgrade Path</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">${upgradePathItems}</div>
            ${animalItems}
            <h3 class="mt-6">Special Items</h3><div class="grid grid-cols-1 md:grid-cols-3 gap-4">${specialItems}</div>`;
}

/**
 * Generates HTML for the saved games list in the settings page.
 * @param {Array<object>} saveSlots - An array of save slot objects.
 * @param {object} playerStats - The player's statistics object.
 * @returns {string} The generated HTML string.
 */
export const gameTypeDisplayNames = {
    [GAME_TYPE_QUICK_CLICK]: 'Quick Game',
    [GAME_TYPE_CAMPAIGN_CLICK]: 'Campaign',
    [GAME_TYPE_CAMPAIGN_ROLLING]: 'Rolling Campaign',
    [GAME_TYPE_AUTOSAVE_QUICK]: 'Autosave (Quick Game)',
    [GAME_TYPE_AUTOSAVE_CAMPAIGN]: 'Autosave (Campaign)',
    [GAME_TYPE_AUTOSAVE_ROLLING]: 'Autosave (Rolling)',
};

export function getSettingsPageHtml(saveSlots, playerStats) {
    let slotsHtml;
    if (saveSlots.length === 0) {
        slotsHtml = '<p>No saved games found.</p>';
    } else {
        slotsHtml = `
            <ul class="space-y-2">
                ${saveSlots.map(slot => `
                    <li class="flex justify-between items-center p-2 border-b border-gray-600">
                        <div>
                            <span class="font-bold">${gameTypeDisplayNames[slot.type] || slot.type}</span>
                            <span class="text-sm text-gray-400">- Level ${slot.level !== null ? slot.level + 1 : 'N/A'} - Saved: ${new Date(slot.updatedAt).toLocaleString()}</span>
                        </div>
                        <div>
                            <button data-slot-id="${slot.id}" class="button-style button-primary load-game-btn">Load</button>
                            <button data-slot-id="${slot.id}" class="button-style button-danger delete-game-btn">Delete</button>
                        </div>
                    </li>
                `).join('')}
            </ul>
        `;
    }

    const achievementProgressHtml = Object.keys(playerStats.achievementProgress || {}).length > 0
        ? `
            <h4 class="font-semibold mt-2 text-gray-400">Achievement Progress:</h4>
            <ul class="list-disc list-inside text-gray-400 text-sm ml-4">
                ${Object.entries(playerStats.achievementProgress).map(([key, value]) => `<li>${key}: ${value}</li>`).join('')}
            </ul>
        ` : '';

    const statsSummaryHtml = `
        <div class="mt-8 border-t border-gray-600 pt-4">
            <h3 class="text-lg font-semibold">Player Stats Summary</h3>
            <ul class="list-disc list-inside text-gray-300 mt-2">
                <li>💰 Gold: ${playerStats.gold.toLocaleString()}</li>
                <li>🏆 Achievements Unlocked: ${playerStats.unlockedAchievements.size}</li>
                <li>Total Merges Made: ${(playerStats.totalMerges || 0).toLocaleString()}</li>
                <li>Disasters Survived: ${(playerStats.totalDisastersSurvived || 0).toLocaleString()}</li>
                <li>Tutorial Completed: ${playerStats.hasPlayedBefore ? 'Yes' : 'No'}</li>
                <li>Tooltips Seen: ${playerStats.seenTooltips.size}</li>
            </ul>
            ${achievementProgressHtml}
        </div>
    `;

    const profileManagementHtml = `
        <h3 class="mt-6 border-t border-gray-600 pt-4">Reset Profile</h3>
        <p class="text-sm text-gray-400 mb-2">This will reset all your gold and achievements. This action cannot be undone.</p>
        <button id="reset-profile-btn" class="button-style button-danger">Reset Profile</button>
    `;

    return `<h3>Saved Games</h3>
            ${slotsHtml}
            ${statsSummaryHtml}
            ${profileManagementHtml}`;
}

/**
 * Generates HTML for a single save state snapshot.
 * @param {object} savedSlot - The save slot object.
 * @param {object} biomesConfig - The biomes configuration object.
 * @param {object} masterItemsConfig - The master items configuration.
 * @returns {string} The generated HTML string.
 */
export function getSaveStateSnapshotHtml(savedSlot, biomesConfig, masterItemsConfig) {
    if (!savedSlot || !savedSlot.gameState) return '';

    const { gameState, type, level, updatedAt } = savedSlot;
    const { configSettings = {}, gameData } = gameState; // Default configSettings for older saves
    const { biomeKey, pathLength, gridSize } = configSettings;
    const { score, gridData } = gameData;
    const biome = biomesConfig[biomeKey];
    const pathInfo = biome.upgradePathLinear.find(p => p.key === pathLength);

    const gridCellsHtml = gridData ? gridData.flat().map(cellData => {
        let content = '';
        let style = '';
        if (cellData && cellData.k) {
            const itemDetails = masterItemsConfig[cellData.k];
            if (itemDetails) {
                content = itemDetails.display;
                style = `background-color: ${itemDetails.style.backgroundColor};`;
            }
        } else if (cellData && cellData.d) {
            content = cellData.d;
        }
        return `<div class="cell-snapshot" style="${style}">${content}</div>`;
    }).join('') : '';

    const totalGridSize = gridData ? gridData.length : gridSize; // Use the actual grid data size
    const gridContainerStyle = `display: grid; grid-template-columns: repeat(${totalGridSize}, 1fr);`;

    const optionsSummary = [
        (configSettings.isDisastersEnabled ?? true) ? 'Disasters' : null,
        (configSettings.isRandomQueueEnabled ?? true) ? 'Random Queue' : 'Fixed Queue',
        (configSettings.isNpcsEnabled ?? true) ? 'NPCs' : null,
        (configSettings.isStationaryObjectsEnabled ?? true) ? 'Obstacles' : null,
        (configSettings.isAnimalsEnabled ?? true) ? 'Animals' : null,
        (configSettings.isPowerupsEnabled ?? true) ? 'Power-ups' : null
    ].filter(Boolean).join(', ');

    return `
        <div class="save-snapshot">
            <h4 class="font-bold text-lg">${type} Game</h4>
            <p>Level: ${level !== null ? level + 1 : 'N/A'}</p>
            <p>Score: ${(score || 0).toLocaleString()}</p>
            <p>Biome: ${biome.name}</p>
            <p>Mode: ${configSettings.gameMode === 'crafting' ? 'Crafting' : `Linear (${pathInfo?.name || 'Default'})`}</p>
            <p class="text-xs text-gray-400">Options: ${optionsSummary}</p>
            <p>Saved: ${new Date(updatedAt).toLocaleString()}</p>
            <div class="snapshot-grid-container mt-2" style="${gridContainerStyle}">${gridCellsHtml}</div>
        </div>
    `;
}

export function getInitialsPromptHtml(score, textConfig) {
    return `
        <p class="text-lg font-bold text-yellow-300">${textConfig.prompts.newHighScore.replace('{SCORE}', (score || 0).toLocaleString())}</p>
        <p class="my-2">${textConfig.prompts.enterInitials}</p>
        <input type="text" id="initials-input" maxlength="3" class="bg-gray-700 border border-gray-500 rounded px-2 py-1 text-center uppercase w-24">
        <button id="save-score-button" class="button-style mt-2">${textConfig.prompts.saveButton}</button>
    `;
}

/**
 * Generates HTML for the high scores list.
 * @param {Array<{score: number, initials: string}>} scores - The array of high score objects.
 * @param {object} textConfig - The text configuration object.
 * @returns {string} The generated HTML string for the list items.
 */
export function getHighScoresHtml(scores, textConfig) {
    if (!scores || scores.length === 0) {
        return `<li style="list-style-type: none;">${textConfig.ui.noHighScores}</li>`;
    }

    return scores.map(score => `
        <li>
            <span class="font-bold text-yellow-300">${score.initials}</span> - ${score.score.toLocaleString()}
        </li>
    `).join('');
}

/**
 * Generates HTML for the level complete page.
 * @returns {string} The generated HTML string.
 */
export function getLevelCompleteHtml() {
    return `
        <h2 class="text-2xl font-bold">Level Complete!</h2>
        <p class="my-4">Excellent work! You are ready for the next challenge.</p>
        <div class="flex justify-center gap-4">
            <button id="next-level-btn" class="button-style button-primary">Next Level</button>
        </div>
    `;
}

/**
 * Generates HTML for the achievements list.
 * @param {object} allAchievements - The configuration object for all achievements.
 * @param {Set<string>} unlockedKeys - A Set containing the keys of unlocked achievements.
 * @param {Object.<string, number>} progressData - An object mapping achievement keys to their current count.
 * @returns {string} The generated HTML string.
 */
export function getAchievementsHtml(allAchievements, unlockedKeys, progressData) {
    return Object.values(allAchievements).map(ach => {
        const isUnlocked = unlockedKeys.has(ach.key);
        // Add a specific class, data attribute, and cursor styles for unlocked achievements
        const unlockedClass = isUnlocked ? 'unlocked-achievement border-l-4 border-green-500 cursor-pointer hover:bg-gray-600' : 'opacity-40';
        const dataAttribute = isUnlocked ? `data-achievement-key="${ach.key}"` : '';
        const icon = isUnlocked ? ach.icon : '❓';
        const name = isUnlocked ? ach.name : 'Locked Achievement';
        let description = isUnlocked ? ach.description : 'Keep playing to unlock!';

        // Add progress display for count-based achievements that are not yet unlocked
        const targetCount = ach.trigger.targetCount;
        if (targetCount > 1 && !isUnlocked) {
            const currentCount = progressData[ach.key] || 0;
            description += ` (${currentCount}/${targetCount})`;
        }

        return `
            <div class="flex items-center mb-4 bg-gray-700 p-4 rounded-lg ${unlockedClass}" ${dataAttribute}>
                <div class="text-4xl mr-4">${icon}</div>
                <div>
                    <h3 class="font-bold text-lg">${name}</h3>
                    <p class="text-sm text-gray-300">${description}</p>
                </div>
            </div>
        `;
    }).join('');
}